// src/services/loanService.js
//
// CRUD for loan records (companies/{cid}/loans — PRD_LoansManagement C4–C7).
// The Loans page and the wizard's Loans step read through here; the page writes
// through here. (An Employee Detail tab was built and withdrawn at /test-impl —
// C12; this service has no other consumer.)
//
// The LEDGER (paidTotal / paidByMonth / payments) is written only by the Approve
// write-back (payrollCompletionService.applyLoanWriteBack) and by
// recordLoanCorrection below — both under payroll:approve, both by blind
// increment + arrayUnion. Every other writer here leaves those fields alone;
// the rules pin enforces it (C6).
//
// firstDeductionMonth is a 'YYYY-MM' string; paidAt on a ledger entry is an
// ISO instant (an event time, not a calendar boundary).

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
  increment,
  arrayUnion,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { loadRunEmployees } from './payrollRunStore';
import { num } from '../utils/loanMath';

export const loansCollection = (companyId) => collection(db, 'companies', companyId, 'loans');

// C14d — exact.
// C16 — a loan carried by a run that is awaiting approval is IN FLIGHT: that run
// already computed an employee's net against it. Deleting it, or moving any of its
// money fields, changes a figure the run has committed to, and the employee is
// paid either way. Mirrors the hard lock time records already get while a run is
// pending ([dtrLockDerivation.js:23](src/services/dtrLockDerivation.js#L23)).
// Pause and Close stay open — neither changes an amount the pending run computed.
//
// NOT enforceable in firestore.rules: a rule cannot scan every pending run for a
// reference to this loan. The paid-lock above IS rules-enforced and still guards
// anything already collected; this one guards money in flight, in the app only.
export const PENDING_RUN_LOCKED =
  "A payroll run awaiting approval includes this loan. Approve or reject that run first.";
// C14(d) — deleting does NOT refund: money already deducted stays deducted, so
// removing the loan would only erase the record of it. Close keeps the history
// and stops future deductions (founder, /test-impl 2026-09-28).
export const DELETE_BLOCKED = "Payroll has already deducted money against this loan, so its payment history should not be deleted. Close it instead: deductions stop and the payment history stays.";
// C16 — loans access does not imply payroll:view, so a loans clerk cannot read
// pending runs at all. Both the delete and the money fields are refused, with
// the reason the account can act on rather than a claim that a run is pending.
// Delete points at Close, which the clerk CAN do, stops every future deduction,
// and still credits a run already in flight (a closed loan stays creditable, C7).
// "an administrator", not "someone with payroll access": payroll permissions do
// not open this page (the nav and route gate on loan:view / loan:edit), so only
// an account holding BOTH can act on it (founder, /test-impl 2026-09-26).
export const PENDING_UNKNOWN =
  "Deleting needs access to payroll runs, which this account doesn't have. Close it instead, or ask an administrator.";
export const PENDING_UNKNOWN_EDIT =
  "Changing amounts needs access to payroll runs, which this account doesn't have. Ask an administrator. The name and notes are still editable.";
// C6 paid-lock (client mirror of the rules clause).
export const PAID_BEFORE_LOCKED = 'Payments have been recorded, so the amount already deducted can no longer change.';
export const TOTAL_BELOW_COLLECTED = 'Loan Amount can only be raised once payments have been recorded.';

const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;
const is2dp = (n) => Math.round(n * 100) / 100 === n;

/**
 * Client-side validation, mirroring loanAmountsValid() in firestore.rules.
 * @param {Object} data - the form values (numbers already parsed)
 * @returns {string|null} the first error, or null
 */
export const validateLoan = (data) => {
  if (!data?.employeeId) return 'Select an employee.';
  if (!(data.name || '').trim()) return 'Enter a loan name.';
  const total = num(data.totalPayable);
  if (!(total > 0)) return 'Loan Amount must be greater than zero.';
  if (!is2dp(total)) return 'Loan Amount can have at most two decimal places.';
  const monthly = num(data.monthlyAmortization);
  if (!(monthly > 0)) return 'Monthly deduction must be greater than zero.';
  if (!is2dp(monthly)) return 'Monthly deduction can have at most two decimal places.';
  const before = num(data.paidBefore);
  if (before < 0) return 'Already deducted cannot be negative.';
  if (!is2dp(before)) return 'Already deducted can have at most two decimal places.';
  if (before >= total) return 'Already deducted must be less than the Loan Amount.';
  if (!MONTH_RE.test(data.firstDeductionMonth || '')) return 'Pick the first deduction month.';
  return null;
};

/**
 * The ONE loan document literal (the BulkAdjustments C2/C3 rule): every
 * creation path builds its document here. Pure apart from serverTimestamp();
 * exported for the Tier-1 suite, which pins the exact key list.
 *
 * @param {Object} data - validated form values
 * @param {string} userId - author uid
 * @returns {Object} the document to write
 */
export const buildLoanDoc = (data, userId) => ({
  employeeId: String(data.employeeId),
  name: (data.name || '').trim(),
  totalPayable: num(data.totalPayable),              // what will be deducted in total (C4)
  monthlyAmortization: num(data.monthlyAmortization),
  firstDeductionMonth: data.firstDeductionMonth,     // 'YYYY-MM'
  paidBefore: num(data.paidBefore),
  notes: (data.notes || '').trim(),
  // ledger — approve-only thereafter (C5/C6)
  paidTotal: 0,
  paidByMonth: {},
  payments: [],
  // lifecycle (C7)
  paused: false,
  closedAt: null,
  closedBy: null,
  closeReason: null,
  createdAt: serverTimestamp(),
  createdBy: userId,
  updatedAt: null,
  updatedBy: null,
  metadata: { isDeleted: false },
});

/**
 * C16 — every loan id referenced by a run that is awaiting approval.
 *
 * Read from the runs themselves rather than a marker on the loan: a marker would
 * need writing at submit and clearing at approve/reject, and a missed clear locks
 * a loan forever. Pending runs are few (usually none or one), so the cost is one
 * query plus one subcollection read each.
 *
 * Throws when the caller cannot read payrollRuns (the rule requires
 * payroll:view). Callers route that through pendingIdsOrNull below.
 * @returns {Promise<Set<string>>}
 */
export const loanIdsInPendingRuns = async (companyId) => {
  const runs = await getDocs(query(
    collection(db, 'companies', companyId, 'payrollRuns'),
    where('status', '==', 'pending-approval'),
  ));
  const ids = new Set();
  await Promise.all(runs.docs.map(async (runDoc) => {
    const lines = await loadRunEmployees(companyId, runDoc.id, runDoc.data());
    lines.forEach((line) => {
      (Array.isArray(line.deductions?.loans) ? line.deductions.loans : [])
        .forEach((e) => { if (e?.loanId) ids.add(e.loanId); });
    });
  }));
  return ids;
};

/**
 * C16 — the pending set, or null when it could not be read (an account with
 * loans access but no payroll:view, or a failed read).
 *
 * Null means LOCKED. The amount a pending run PAYS is fixed — the write-back
 * credits the figure stored on the run's own line, never the live loan
 * (payrollCompletionService.js:257-260) — but the amount it CREDITS is applied
 * with increment() against no ceiling (:313) while loanRemaining floors at 0
 * (loanMath.js:31). So moving paidBefore or totalPayable under a pending run
 * over-deducts the employee and the balance absorbs it silently. This matches
 * dtrLockDerivation.js, the precedent C16 cites, which also fails closed
 * (/impl-review round 2, 2026-09-27).
 */
const pendingIdsOrNull = async (companyId) => {
  try {
    return await loanIdsInPendingRuns(companyId);
  } catch (err) {
    return null;
  }
};

// C16 — the fields a pending run has already computed against.
const MONEY_FIELDS = ['totalPayable', 'monthlyAmortization', 'paidBefore', 'firstDeductionMonth'];

// The fields the loan form may change on an existing record (C6 — never the ledger).
// C4 — the six editable fields. `referenceNo`, `principal`, `termMonths` and
// `loanDate` were cut at /test-impl 2026-09-24 as clutter on a form meant to
// suit any loan. `principal`, `termMonths` and `loanDate` had no reader;
// `referenceNo` had two (the list's search haystack and a muted row subtitle),
// and both were removed with it — the earlier claim that none of the four had a
// reader was wrong (/impl-review r2, 2026-09-27).
const EDITABLE = [
  'name', 'totalPayable', 'monthlyAmortization', 'firstDeductionMonth', 'paidBefore', 'notes',
];

const snapToRows = (snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }));

export const getLoan = async (companyId, loanId) => {
  const snap = await getDoc(doc(loansCollection(companyId), loanId));
  if (!snap.exists()) throw new Error('Loan not found.');
  return { id: snap.id, ...snap.data() };
};

/**
 * Create a loan (C4). Validates, then writes the literal.
 * @returns {Promise<string>} the new loan id
 */
export const createLoan = async (companyId, data, userId) => {
  const error = validateLoan(data);
  if (error) throw new Error(error);
  const ref = await addDoc(loansCollection(companyId), buildLoanDoc(data, userId));
  return ref.id;
};

/**
 * Update the editable fields of a loan (C4/C6). Re-reads the stored doc first
 * (a stale tab must not edit off an old copy), validates the merged record and
 * mirrors the rules' paid-lock so the user meets a plain message, not a raw
 * permission error. Never touches the ledger.
 */
export const updateLoan = async (companyId, loanId, updates, userId) => {
  const existing = await getLoan(companyId, loanId);
  // C16 — the money fields are frozen while a pending run references the loan.
  // Name and notes stay editable: neither moves a figure the run computed.
  if (MONEY_FIELDS.some((f) => updates[f] !== undefined && updates[f] !== existing[f])) {
    const pending = await pendingIdsOrNull(companyId);
    if (pending === null) throw new Error(PENDING_UNKNOWN_EDIT);
    if (pending.has(loanId)) throw new Error(PENDING_RUN_LOCKED);
  }
  const merged = { ...existing, ...updates };
  const error = validateLoan(merged);
  if (error) throw new Error(error);
  const paidTotal = num(existing.paidTotal);
  if (paidTotal > 0) {
    if (updates.paidBefore !== undefined && num(updates.paidBefore) !== num(existing.paidBefore)) {
      throw new Error(PAID_BEFORE_LOCKED);
    }
    if (num(merged.totalPayable) < num(existing.paidBefore) + paidTotal) {
      throw new Error(TOTAL_BELOW_COLLECTED);
    }
  }
  const built = buildLoanDoc(merged, userId);
  const write = { updatedAt: serverTimestamp(), updatedBy: userId };
  EDITABLE.forEach((f) => { if (updates[f] !== undefined) write[f] = built[f]; });
  await updateDoc(doc(loansCollection(companyId), loanId), write);
};

/** C7 — pause / resume: the loan keeps its balance but leaves the Loans step. */
export const setLoanPaused = async (companyId, loanId, paused, userId) => {
  await updateDoc(doc(loansCollection(companyId), loanId), {
    paused: Boolean(paused),
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  });
};

/** C7 — manual close, for a loan settled outside payroll. Not reversible in v1. */
export const closeLoan = async (companyId, loanId, reason, userId) => {
  await updateDoc(doc(loansCollection(companyId), loanId), {
    closedAt: serverTimestamp(),
    closedBy: userId,
    closeReason: (reason || '').trim() || null,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  });
};

/**
 * C7 — void (soft delete) a data-entry mistake. Blocked once payroll has paid
 * against the loan; the rules deny it too (C6) — this is the plain-language
 * message in front of that denial.
 */
export const voidLoan = async (companyId, loanId, userId) => {
  const fresh = await getLoan(companyId, loanId);
  if (num(fresh.paidTotal) > 0) throw new Error(DELETE_BLOCKED);
  const pending = await pendingIdsOrNull(companyId); // C16
  if (pending === null) throw new Error(PENDING_UNKNOWN);
  if (pending.has(loanId)) throw new Error(PENDING_RUN_LOCKED);
  await updateDoc(doc(loansCollection(companyId), loanId), {
    'metadata.isDeleted': true,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  });
};

/**
 * C5 — the approver's correction: the same three ledger fields the write-back
 * writes, with runId null and a required reason. Signed amount; a negative one
 * gives the balance back. It is the only hand-write to the ledger and it is
 * fully visible in the history.
 */
export const recordLoanCorrection = async (companyId, loanId, { amount, monthKey, reason }, userId) => {
  const a = num(amount);
  if (a === 0) throw new Error('Enter a non-zero amount.');
  if (!is2dp(a)) throw new Error('Amount can have at most two decimal places.');
  if (!MONTH_RE.test(monthKey || '')) throw new Error('Pick the month the correction applies to.');
  if (!(reason || '').trim()) throw new Error('A reason is required.');
  await updateDoc(doc(loansCollection(companyId), loanId), {
    paidTotal: increment(a),
    [`paidByMonth.${monthKey}`]: increment(a),
    payments: arrayUnion({
      runId: null,
      periodStart: null,
      periodEnd: null,
      monthKey,
      amount: a,
      paidAt: new Date().toISOString(),
      by: userId,
      reason: reason.trim(),
    }),
    updatedAt: serverTimestamp(),
  });
};

/**
 * C13 — every non-voided loan in the company, one single-field query (no
 * composite index). The page, the Loans step and the Approve write-back all
 * read this; callers filter further (qualifies / isCreditable) client-side.
 */
export const fetchCompanyLoans = async (companyId) => {
  const snap = await getDocs(
    query(loansCollection(companyId), where('metadata.isDeleted', '==', false)),
  );
  return snapToRows(snap);
};

